import React, { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { linkify } from '@/lib/linkify';
import { usePresentation } from '@/context/PresentationContext';
import { FreeText, MemberName } from '@/components/Present';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, HourglassMedium, WarningOctagon, CalendarBlank, Checks, CheckCircle, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = {
    waiting: {
        label: 'Waiting to hear back',
        icon: HourglassMedium,
        accent: 'text-primary',
        empty: 'Nothing waiting on a callback right now.',
    },
    attention: {
        label: 'Needs your attention',
        icon: WarningOctagon,
        accent: 'text-[#F59E0B]',
        empty: 'Nothing flagged right now — take a breath.',
    },
    upcoming: {
        label: 'Coming up this week',
        icon: CalendarBlank,
        accent: 'text-primary',
        empty: 'Nothing on the calendar this week.',
    },
    todos: {
        label: 'To-dos',
        icon: Checks,
        accent: 'text-primary',
        empty: 'No open to-dos. Nice work.',
    },
};

function daysBetween(iso) {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function ResolveDoneButton({ log, onDone, testid = 'resolve-log' }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const qc = useQueryClient();
    const submit = async (skipNote = false) => {
        try {
            await api.post(`/patient-logs/${log.log_id}/resolve`, { resolution_note: skipNote ? '' : note.trim() });
            toast.success('Marked as done');
            setOpen(false); setNote('');
            // Refresh the dashboard summary + awaiting list wherever this button lives.
            qc.invalidateQueries({ queryKey: ['dashboard'] });
            qc.invalidateQueries({ queryKey: ['awaiting-response'] });
            onDone?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Button
                size="sm"
                data-testid={testid}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 min-h-[44px] sm:min-h-0"
            >
                <CheckCircle size={14} weight="duotone" className="mr-1" /> Mark as Done
            </Button>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl">How did it resolve?</DialogTitle></DialogHeader>
                <div className="text-sm text-slate-600 whitespace-pre-wrap max-h-40 overflow-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <FreeText>{linkify(log.note)}</FreeText>
                </div>
                <Textarea
                    data-testid="resolve-note"
                    placeholder="Optional — one line about how you closed it out."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={2}
                />
                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={() => submit(true)} className="rounded-full">Skip and mark done</Button>
                    <Button data-testid="resolve-confirm" onClick={() => submit(false)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                        Save & mark done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ResolveDoneButton };

function WaitingList({ items, refresh }) {
    const sorted = [...items].sort((a, b) => (a.entry_at || '').localeCompare(b.entry_at || ''));
    return (
        <div className="rounded-xl border border-slate-200 bg-card divide-y divide-slate-200">
            {sorted.map(l => {
                const d = daysBetween(l.entry_at);
                const overdue = d > 5;
                return (
                    <div key={l.log_id} data-testid="waiting-row" className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="min-w-0 flex-1">
                            <Link to={`/members/${l.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                                <MemberName name={l.patient_name} />
                            </Link>
                            <div className={`text-xs mt-0.5 ${overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                                Waiting {d} day{d === 1 ? '' : 's'}{overdue && ' — overdue'}
                            </div>
                            <div className="text-sm text-slate-700 mt-1 line-clamp-2 whitespace-pre-wrap">{l.note}</div>
                        </div>
                        <div className="flex items-center gap-2 sm:shrink-0 flex-wrap">
                            <Button asChild variant="outline" size="sm" className="rounded-full min-h-[44px] sm:min-h-0">
                                <Link to={`/members/${l.patient_id}`}>Open</Link>
                            </Button>
                            <ResolveDoneButton log={l} onDone={refresh} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function AttentionList({ items, refresh }) {
    const [openReview, setOpenReview] = useState(null);
    const submitReview = async (note) => {
        try {
            await api.post(`/patient-logs/${openReview.log_id}/review`, { note: note || '' });
            toast.success('Reviewed');
            setOpenReview(null); refresh?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <div className="rounded-xl border border-slate-200 bg-card divide-y divide-slate-200">
            {items.map(l => (
                <div key={l.log_id} data-testid="attention-row" className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#F59E0B] text-black mt-1 shrink-0">! Attention</span>
                        <div className="min-w-0 flex-1">
                            <Link to={`/members/${l.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline">
                                <MemberName name={l.patient_name} />
                            </Link>
                            <div className="text-xs text-slate-500">{l.entry_type} · {new Date(l.entry_at).toLocaleString()}</div>
                            <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap line-clamp-3">{l.note}</div>
                        </div>
                    </div>
                    <Button
                        size="sm" data-testid="review-attention"
                        onClick={() => setOpenReview(l)}
                        className="rounded-full bg-primary text-primary-foreground shrink-0 min-h-[44px] sm:min-h-0"
                    >
                        Mark as reviewed
                    </Button>
                </div>
            ))}
            <ReviewDialog log={openReview} onClose={() => setOpenReview(null)} onSubmit={submitReview} />
        </div>
    );
}

function ReviewDialog({ log, onClose, onSubmit }) {
    const [note, setNote] = useState('');
    if (!log) return null;
    return (
        <Dialog open={!!log} onOpenChange={(v) => !v && onClose()}>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl">Mark this as reviewed</DialogTitle></DialogHeader>
                <div className="text-sm text-slate-600 whitespace-pre-wrap max-h-40 overflow-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <FreeText>{linkify(log.note)}</FreeText>
                </div>
                <Textarea placeholder="Optional note…" value={note} onChange={e => setNote(e.target.value)} rows={2} />
                <DialogFooter>
                    <Button onClick={() => onSubmit(note)} className="rounded-full bg-primary text-primary-foreground">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UpcomingList({ items }) {
    const sorted = [...items].sort((a, b) => (a.scheduled_at || '').localeCompare(b.scheduled_at || ''));
    return (
        <div className="rounded-xl border border-slate-200 bg-card divide-y divide-slate-200">
            {sorted.map(a => (
                <div key={a.appointment_id} data-testid="upcoming-row" className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="min-w-[100px] shrink-0">
                            <div className="font-heading text-2xl text-slate-900">{new Date(a.scheduled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                            <div className="text-sm text-primary">{new Date(a.scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <Link to={`/members/${a.patient_id}`} className="text-sm font-medium text-slate-900 hover:underline"><MemberName name={a.patient_name} /></Link>
                            <div className="text-sm text-slate-700 mt-0.5">{a.title}</div>
                            <div className="text-xs text-slate-500">{a.provider_name || 'Doctor'}</div>
                        </div>
                    </div>
                    <Button asChild variant="outline" size="sm" className="rounded-full shrink-0 min-h-[44px] sm:min-h-0">
                        <Link data-testid="upcoming-prep-link" to={`/prep/${a.appointment_id}`}>
                            <FilePdf size={14} className="mr-1" /> Prep for this visit
                        </Link>
                    </Button>
                </div>
            ))}
        </div>
    );
}

function TodosList({ items, refresh }) {
    const sorted = [...items].sort((a, b) => (a.due_date || 'z').localeCompare(b.due_date || 'z'));
    const toggle = async (t) => {
        try {
            await api.patch(`/tasks/${t.task_id}`, { status: t.status === 'Done' ? 'Open' : 'Done' });
            refresh?.();
        } catch { toast.error('Failed'); }
    };
    const today = new Date().toISOString().slice(0, 10);
    return (
        <div className="rounded-xl border border-slate-200 bg-card divide-y divide-slate-200">
            {sorted.map(t => {
                const overdue = t.due_date && t.due_date < today;
                return (
                    <div key={t.task_id} data-testid="todo-row" className="px-4 sm:px-5 py-4 flex items-center gap-3">
                        <input
                            type="checkbox"
                            data-testid="todo-done"
                            checked={t.status === 'Done'}
                            onChange={() => toggle(t)}
                            className="h-6 w-6 accent-emerald-600 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                            <Link to={`/members/${t.patient_id}`} className="text-sm text-slate-900 hover:underline">
                                {t.title}
                            </Link>
                            <div className={`text-xs mt-0.5 ${overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                                <MemberName name={t.patient_name} />{t.due_date && <> · due {t.due_date}{overdue ? ' (overdue)' : ''}</>}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function DashboardCategory() {
    const { category } = useParams();
    const cfg = CATEGORIES[category];
    const qc = useQueryClient();
    const [waitingLimit, setWaitingLimit] = useState(10);
    const { data, isLoading } = useQuery({
        queryKey: ['dashboard'],
        queryFn: async () => (await api.get('/dashboard/summary')).data,
    });
    // For "waiting" we also need ALL Awaiting Response notes, not just stale ones.
    // Paginated: 20 at a time with a total for the header.
    const { data: waitingData = { items: [], total: 0 } } = useQuery({
        queryKey: ['awaiting-response', waitingLimit],
        queryFn: async () => (await api.get('/patient-logs', {
            params: { status: 'Awaiting Response', limit: waitingLimit, include_total: true }
        })).data,
        enabled: category === 'waiting',
    });
    const awaiting = waitingData.items || [];
    const awaitingTotal = waitingData.total || 0;
    // For "todos" we need ALL open tasks not just overdue
    const { data: openTasks = [] } = useQuery({
        queryKey: ['open-tasks'],
        queryFn: async () => (await api.get('/tasks', { params: { only_open: 'true' } })).data,
        enabled: category === 'todos',
    });
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['dashboard'] });
        qc.invalidateQueries({ queryKey: ['awaiting-response'] });
        qc.invalidateQueries({ queryKey: ['open-tasks'] });
    };

    if (!cfg) return <Navigate to="/dashboard" replace />;
    if (isLoading || !data) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

    let items = [];
    let total = 0;
    let Body = null;
    let loadMore = null;
    if (category === 'waiting') {
        items = awaiting;
        total = awaitingTotal;
        Body = <WaitingList items={items} refresh={refresh} />;
        if (items.length < total) {
            loadMore = (
                <div className="mt-4 flex justify-center">
                    <Button
                        data-testid="waiting-load-more"
                        variant="outline"
                        className="rounded-full min-h-[44px]"
                        onClick={() => setWaitingLimit(waitingLimit + 10)}
                    >
                        Load more ({total - items.length} remaining)
                    </Button>
                </div>
            );
        }
    } else if (category === 'attention') { items = data.flagged_logs || []; total = items.length; Body = <AttentionList items={items} refresh={refresh} />; }
    else if (category === 'upcoming') { items = data.upcoming_appointments || []; total = items.length; Body = <UpcomingList items={items} />; }
    else if (category === 'todos') { items = openTasks; total = items.length; Body = <TodosList items={items} refresh={refresh} />; }
    const Icon = cfg.icon;

    return (
        <div data-testid={`dashboard-category-${category}`} className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-4xl">
            <Link to="/dashboard" data-testid="back-to-home" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4">
                <ArrowLeft size={12} /> Back to home
            </Link>
            <header className="mb-6 flex items-end justify-between gap-4">
                <div className="min-w-0">
                    <div className={`text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 ${cfg.accent}`}>
                        <Icon size={12} weight="duotone" /> Category
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-slate-900 mt-1 break-words">{cfg.label}</h1>
                </div>
                <span data-testid="dashboard-category-count" className="text-3xl font-heading text-slate-900 shrink-0">{total}</span>
            </header>

            {total === 0 ? (
                <div data-testid="dashboard-category-empty" className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
                    {cfg.empty}
                </div>
            ) : (
                <>
                    {Body}
                    {loadMore}
                </>
            )}
        </div>
    );
}
